import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "../components/IconButton";
import { IconDownload } from "../components/icons";
import { downloadAuthenticated, fetchAuthenticatedBytes, fetchAuthenticatedText } from "../lib/download";
import { prepareHtmlPreview } from "../lib/html-preview";
import { renderMarkdown } from "../lib/markdown";
import { usePageRefresh } from "../lib/page-lifecycle";
import { renderPdfToHost } from "../lib/pdf-preview";

type DocMode = "html" | "pdf" | "md";

const FILES: Record<DocMode, { preview: string; download: string; filename: string; label: string }> = {
  html: {
    preview: "/api/developers/docs/html",
    download: "/api/developers/docs/html/download",
    filename: "dashcam-api-v1.html",
    label: "HTML",
  },
  pdf: {
    preview: "/api/developers/docs/pdf",
    download: "/api/developers/docs/pdf/download",
    filename: "dashcam-api-v1.pdf",
    label: "PDF",
  },
  md: {
    preview: "/api/developers/docs/guide",
    download: "/api/developers/docs/guide/download",
    filename: "dashcam-api-v1.md",
    label: "MD",
  },
};

export function DocsPage() {
  const [mode, setMode] = useState<DocMode>("html");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState<string | null>(null);
  const [markdownHtml, setMarkdownHtml] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const canvasHost = useRef<HTMLDivElement>(null);
  const pdfBytes = useRef<Uint8Array | null>(null);

  usePageRefresh(
    useCallback(async () => {
      setReloadNonce((n) => n + 1);
    }, []),
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setStatus(null);
    setHtml(null);
    setMarkdownHtml(null);
    pdfBytes.current = null;
    setLoading(true);
    if (canvasHost.current) canvasHost.current.replaceChildren();

    const load = async () => {
      try {
        if (mode === "html") {
          const text = prepareHtmlPreview(await fetchAuthenticatedText(FILES.html.preview));
          if (!cancelled) setHtml(text);
          return;
        }
        if (mode === "md") {
          const text = await fetchAuthenticatedText(FILES.md.preview);
          if (!cancelled) setMarkdownHtml(renderMarkdown(text));
          return;
        }
        const bytes = await fetchAuthenticatedBytes(FILES.pdf.preview);
        if (cancelled) return;
        pdfBytes.current = bytes;
        const host = canvasHost.current;
        if (!host) throw new Error("PDF preview is not ready.");
        await renderPdfToHost(host, bytes);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not open the document.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [mode, reloadNonce]);

  useEffect(() => {
    if (mode !== "pdf" || loading) return;
    const host = canvasHost.current;
    if (!host) return;
    let frame = 0;
    let lastWidth = host.clientWidth;
    const observer = new ResizeObserver(() => {
      if (Math.abs(host.clientWidth - lastWidth) < 12) return;
      lastWidth = host.clientWidth;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const bytes = pdfBytes.current;
        if (!bytes || host.clientWidth < 40) return;
        void renderPdfToHost(host, bytes).catch(() => {
          /* Keep the last successful render if a resize redraw fails. */
        });
      });
    });
    observer.observe(host);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [mode, loading]);

  const download = async (kind: DocMode) => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const file = FILES[kind];
      const result = await downloadAuthenticated(file.download, file.filename);
      setStatus(
        result.saved
          ? `Saved ${file.filename} to ${result.folder ?? "Downloads"}`
          : `Downloading ${file.filename}…`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="docs-shell docs-shell-mobile">
      <section className="docs-toolbar">
        <div className="docs-mode" role="tablist" aria-label="Documentation format">
          {(["html", "pdf", "md"] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={mode === id}
              className={mode === id ? "primary" : ""}
              onClick={() => setMode(id)}
            >
              {FILES[id].label}
            </button>
          ))}
        </div>
        <div className="docs-toolbar-actions">
          {(["pdf", "html", "md"] as const).map((id) => (
            <IconButton key={id} label={`Download ${FILES[id].label}`} disabled={busy} onClick={() => void download(id)}>
              <IconDownload />
              <span className="icon-btn-label">{FILES[id].label}</span>
            </IconButton>
          ))}
        </div>
      </section>
      {error && <p className="form-error">{error}</p>}
      {status && <p className="form-success">{status}</p>}
      <div className="docs-frame-wrap docs-preview-wrap">
        {loading && <p className="empty-grid">Loading {FILES[mode].label} preview…</p>}
        {!loading && mode === "html" && html && (
          <iframe
            title="Dashcam Developer API"
            className="docs-live-frame"
            srcDoc={html}
            sandbox="allow-scripts allow-same-origin allow-clipboard-write"
          />
        )}
        {!loading && mode === "md" && markdownHtml && (
          <article className="docs-md-preview" dangerouslySetInnerHTML={{ __html: markdownHtml }} />
        )}
        <div ref={canvasHost} className="docs-pdf-preview" hidden={mode !== "pdf"} />
      </div>
    </div>
  );
}
